import * as PIXI from 'pixi.js';
import * as PIXIFilters from 'pixi-filters';
import { ProcessSpectrum, GetSpectrumMagnitude } from '../../ProcessSpectrum';
import { CONSTANTS, WAVE_TYPE, WAVE_STYLE, REFLECTION_TYPE, GLOW_TYPE, MOVEMENT_DIRECTION } from '../../PreviewDisplayConstants';
import DriftEffect from '../../effects/drift/DriftEffect';
import { getEffectiveDrift, VISUALIZER_ANCHORS } from '../../effects/drift/driftUtilities';
import chroma from 'chroma-js';
import { average } from '../../../../../../utilities/mathUtilities';

const baseHeight = 500;
const baseRadius = 100;
const fireSpeed = 4;
const WAVE_CIRCLE_DRIFT_ROTATION_GAIN = 2.5;
const EPSILON = 0.000001;

// Inward waves grow toward the center; if a trough reaches it, many vertices collapse onto (0,0)
// and the polygon self-intersects into spikes. Cap the inward amplitude so the trough always keeps
// a small minimum inner radius (a fraction of the ring radius) instead of crossing the center.
const INWARD_MIN_RADIUS_RATIO = 0.02;

const LOGO_GLOW_BLUR_SENSITIVITY = 0.7;
const LOGO_GLOW_SCALE_SENSITIVITY = 0.7;

// The ERASE blend only reads the shape's alpha, so the knockout fill colour is arbitrary.
const CUTOUT_ERASE_COLOR = 0xFFFFFF;

// INNER glow hides the sharp original so only the bloomed clone shows.
const isInnerGlow = (glowType) => glowType === GLOW_TYPE.INNER;

// A hollow-center Solid layer is the band between the ring and the wave contour. Which of the two loops
// is the outer boundary depends on the direction: an outward wave grows past the ring, an inward one
// stays inside it. The other loop is punched out as a hole.
//
// A hole is used rather than a single band polygon closed with a bridge between its two loops, because
// antialiasing renders that bridge as a visible hairline crack across the band.
const orderHollowLoops = (contourPoints, ringPoints, inwards) => ({
    outerLoop: inwards ? ringPoints : contourPoints,
    innerLoop: inwards ? contourPoints : ringPoints
});

export class WaveCircle {
    constructor(stage, zIndex) {
        this.stage = stage;

        this.waveSettings = [];
        this.x = baseHeight * 0.5;
        this.y = baseHeight * 0.5;
        this.offset = { x: 0, y: 0 };
        this.driftEffect = new DriftEffect();

        // Create two main containers.
        this.logoContainer = new PIXI.Container();
        this.visualsContainer = new PIXI.Container();

        // We add 0.1 to see the logo on top of visuals.
        this.logoContainer.zIndex = zIndex + 0.1;
        this.visualsContainer.zIndex = zIndex;

        // "Transparency Cutout" layers knock out the visualizer with an ERASE blend to reveal the
        // background. Each layer owns its own ERASE graphic placed at the layer's z-position (see
        // addNewLayer), so a cutout subtracts only the layers drawn BEHIND it; layers in front draw
        // on top of the hole and stay visible. ERASE only stays inside the visualizer because the
        // visuals container is isolated by cutoutIsolationFilter while any cutout is active (otherwise
        // ERASE would also erase the already-drawn background). A filled polygon is robust to the
        // inward wave collapsing to the center, unlike an earcut hole which mis-triangulates there.
        this.cutoutIsolationFilter = new PIXI.filters.AlphaFilter(1);

        // The layer stack needs an isolating framebuffer of its OWN, not just the shared one above.
        // Without it a cutout's ERASE reaches past the layers and wipes the glow container underneath,
        // which is why the glow could never survive inside a hole. With it, a knockout removes the
        // layers behind it and stops there, leaving the hole transparent so the glow shows through.
        this.layerStackIsolationFilter = new PIXI.filters.AlphaFilter(1);

        // Content containers for each layer will be added here
        this.layerContainers = [];

        // Add content for logo.
        this.logoSprite = new PIXI.Sprite();
        this.logoContainer.addChild(this.logoSprite);

        const logoBloomMultiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;
        this.logoGlowContainer = new PIXI.Container();
        this.logoGlowBloomFilter = new PIXIFilters.AdvancedBloomFilter({
            threshold: 0.01,
            quality: 10
        });
        this.logoGlowBloomFilter.padding = 100 * logoBloomMultiplier;
        this.logoGlowContainer.filters = [this.logoGlowBloomFilter];
        this.logoGlowSprite = new PIXI.Sprite();
        this.logoGlowContainer.addChild(this.logoGlowSprite);
        this.logoGlowContainer.visible = false;

        // Place the glow clone behind the actual logo sprite.
        this.logoContainer.addChildAt(this.logoGlowContainer, 0);

        // Add containers to the main stage.
        this.stage.addChild(this.logoContainer);
        this.stage.addChild(this.visualsContainer);

        this.config = {};
        this.layerEffects = [];
    }

    update(config) {
        // Load the texture for logo
        this.loadImageTexture(config);
        this.config = config;

        // Show or hide the logo.
        if (this.logoContainer.visible != config.logoVisible)
            this.logoContainer.visible = config.logoVisible;

        // Initiation layers
        if (!this.waveCircles)
            this.initializeLayer(config.waveCircles);

        // Update Wave Circle Options
        this.waveSettings = config.waveCircles;

        const logoSize = baseRadius * 2 * this.config.logoSizeFactor;
        const logoPosition = -baseRadius + baseRadius * (1 - this.config.logoSizeFactor);
        this.logoSprite.width = logoSize;
        this.logoSprite.height = logoSize;
        this.logoSprite.x = logoPosition;
        this.logoSprite.y = logoPosition;

        this.logoGlowSprite.width = logoSize;
        this.logoGlowSprite.height = logoSize;
        this.logoGlowSprite.x = logoPosition;
        this.logoGlowSprite.y = logoPosition;

        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;

        this.setPositionAndScale(multiplier);
        this.updateLayerEffects(multiplier);

        // Update the properties for visualizer spinning.
        // The speed properties is RPM value. 
        this.spinSettings = this.config.spinSettings;
        this.spinEnabled = this.config.spinSettings.enabled;
        this.spinLogoLocked = this.config.spinSettings.logoLocked;

        // Default rotation (convert to radians).
        this.baseRotation = this.config.rotation / (180 / Math.PI);
        this.logoContainer.rotation = this.baseRotation;
        this.visualsContainer.rotation = this.baseRotation;

        if (this.config.drift) {
            const effectiveDrift = getEffectiveDrift(this.config.drift, VISUALIZER_ANCHORS);
            this.driftEffect.updateConfig(effectiveDrift);
            this.driftEffect.setBaseTransform(
                { x: this.x, y: this.y },
                this.baseRotation,
                { x: this.scaleAmount, y: this.scaleAmount }
            );
            this.driftEffect.setDimensions(
                baseHeight * this.scaleAmount,
                baseHeight * this.scaleAmount
            );
        }
    }

    updateLayers(waveCircles) {
        this.initializeLayer(waveCircles);
        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;
        this.updateLayerEffects(multiplier);
    }

    // The erase graphic lives at its layer container's origin without the per-layer scale/rotation,
    // so a layer's polygon must be pre-transformed (scale, then rotation) to land at the same
    // on-screen position as the wave it replaces.
    transformLayerPoints(points, rotation, scale) {
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        const transformedPoints = [];

        for (let i = 0; i < points.length; i += 2) {
            const x = points[i] * scale;
            const y = points[i + 1] * scale;
            transformedPoints.push(x * cos - y * sin);
            transformedPoints.push(x * sin + y * cos);
        }

        return transformedPoints;
    }

    // Shared preamble of every cutout style: the layer paints nothing itself, and its knockout shapes
    // must be pre-transformed to the container origin. waveGraphic is only used to derive the layer's
    // rotation and scale for that transform. Returns them for the caller to apply per shape.
    prepareCutoutErase(waveGraphic, settings, calculatedRotation, rotation) {
        waveGraphic.clear();
        this.setRotation(waveGraphic, settings, calculatedRotation, rotation);

        return { layerRotation: waveGraphic.rotation, layerScale: waveGraphic.scale.x };
    }

    // A cutout layer draws nothing itself; the shape it would have drawn is written into the layer's
    // own ERASE graphic (eraseGraphic), which sits at the layer's z-position so it knocks out only the
    // layers BEHIND it, exposing the background there. The caller clears eraseGraphic once per frame
    // (in draw()), so these methods only append shapes.
    //
    // Solid knocks out its filled shape; Bar knocks out each bar; Point knocks out each dot; Line knocks
    // out only its stroke. Every style must have a knockout of its own, otherwise enabling Transparent
    // leaves the wave fully drawn. A knockout always matches what the layer would have painted, so a
    // hollow-center Solid knocks out the band only and leaves its center untouched.
    // Paints one knockout shape into every graphic that has to know about it: the layer's own ERASE in
    // the layer stack, and the matching ERASE in the layer's glow slot. They live in the same container
    // space, so the already-transformed shape is reused verbatim. While the bleed pass is running for a
    // per-layer glow it needs the same shape too, in its own source and in the mask that keeps it inside
    // the hole.
    paintCutoutShape(eraseTargets, paintShape) {
        eraseTargets.forEach(paintShape);

        if (!this.cutoutGlowBleedActive)
            return;

        paintShape(this.cutoutGlowEraseGraphic);
        paintShape(this.cutoutGlowMaskGraphic);
    }

    drawCutoutErase(eraseTargets, waveGraphic, settings, points, calculatedRotation, rotation, hollowOptions) {
        const { layerRotation, layerScale } = this.prepareCutoutErase(waveGraphic, settings, calculatedRotation, rotation);

        const contourPoints = this.transformLayerPoints(points, layerRotation, layerScale);
        const hollowLoops = hollowOptions && orderHollowLoops(
            contourPoints,
            this.transformLayerPoints(hollowOptions.basePoints, layerRotation, layerScale),
            hollowOptions.inwards
        );

        this.paintCutoutShape(eraseTargets, (graphic) => {
            graphic.beginFill(CUTOUT_ERASE_COLOR, 1);
            graphic.drawPolygon(hollowLoops ? hollowLoops.outerLoop : contourPoints);
            if (hollowLoops) {
                graphic.beginHole();
                graphic.drawPolygon(hollowLoops.innerLoop);
                graphic.endHole();
            }
            graphic.endFill();
        });
    }

    // A Line layer paints nothing but its outline, so its knockout is that same stroke. Filling the
    // polygon instead would punch out the whole area the outline encloses.
    drawCutoutEraseLine(eraseTargets, waveGraphic, settings, points, calculatedRotation, rotation) {
        const { layerRotation, layerScale } = this.prepareCutoutErase(waveGraphic, settings, calculatedRotation, rotation);

        const strokePoints = this.transformLayerPoints(points, layerRotation, layerScale);
        // The erase graphic carries no layer scale, so the stroke width has to be scaled by hand to
        // match the outline it replaces.
        const strokeWidth = settings.lineWidth * layerScale;

        this.paintCutoutShape(eraseTargets, (graphic) => {
            graphic.lineStyle(strokeWidth, CUTOUT_ERASE_COLOR, 1);
            graphic.drawPolygon(strokePoints);
            graphic.lineStyle(0);
        });
    }

    drawCutoutEraseBars(eraseTargets, waveGraphic, settings, pointSets, calculatedRotation, rotation) {
        const { layerRotation, layerScale } = this.prepareCutoutErase(waveGraphic, settings, calculatedRotation, rotation);

        const barPolygons = pointSets.map((barPoints) => this.transformLayerPoints(barPoints, layerRotation, layerScale));

        this.paintCutoutShape(eraseTargets, (graphic) => {
            graphic.beginFill(CUTOUT_ERASE_COLOR, 1);
            barPolygons.forEach((barPolygon) => graphic.drawPolygon(barPolygon));
            graphic.endFill();
        });
    }

    drawCutoutErasePoints(eraseTargets, waveGraphic, settings, points, pointRadius, calculatedRotation, rotation) {
        const { layerRotation, layerScale } = this.prepareCutoutErase(waveGraphic, settings, calculatedRotation, rotation);

        const erasePoints = this.transformLayerPoints(points, layerRotation, layerScale);
        // The wave graphic carries the layer scale, but the erase graphic does not, so the dot radius
        // has to be scaled by hand to match the dots this knockout replaces.
        const eraseRadius = pointRadius * layerScale;

        this.paintCutoutShape(eraseTargets, (graphic) => {
            graphic.beginFill(CUTOUT_ERASE_COLOR, 1);
            for (let i = 0; i < erasePoints.length - 1; i += 2) {
                graphic.drawCircle(erasePoints[i], erasePoints[i + 1], eraseRadius);
            }
            graphic.endFill();
        });
    }

    // CUTOUT GLOW BLEED — why a second bloom pass exists.
    //
    // A "Transparent" layer punches a hole through the visualizer, and the global glow is drawn behind
    // the layers, so the hole knocks the glow out along with the fills. The result is a hole with a dead
    // black rim. The glow has to bleed inward from the rim while the hole itself stays a hole.
    //
    // Lifting the shared glow above the knockout does not do that: the bloom keeps the shapes' solid
    // cores, so it simply refills the hole and the mask stops reading as a mask. Instead this pass blooms
    // the same clones with the cutout shapes ERASED out of the source, so the bloom spills into the hole
    // from its rim and nothing solid survives inside it. The pass is then masked to the cutout shapes,
    // which leaves the whole area outside the hole exactly as the regular glow drew it - the wave stays
    // flat under an outer glow, and stays bloomed under an inner one.
    createCutoutGlowBleed(multiplier) {
        this.cutoutGlowMaskGraphic = new PIXI.Graphics();
        this.visualsContainer.addChild(this.cutoutGlowMaskGraphic);

        this.cutoutGlowEraseGraphic = new PIXI.Graphics();
        this.cutoutGlowEraseGraphic.blendMode = PIXI.BLEND_MODES.ERASE;

        this.cutoutGlowBleedBloomFilter = new PIXIFilters.AdvancedBloomFilter({
            threshold: 0.01,
            quality: 10
        });
        this.cutoutGlowBleedBloomFilter.padding = 100 * multiplier;

        // The nesting below is load-bearing. PixiJS pushes a display object's filters BEFORE its mask,
        // so a container carrying both is masked on the way INTO the bloom: the glow source gets clipped
        // to the hole, where it has just been erased, and the pass renders nothing at all. Keeping the
        // bloom one level below the mask clips the bloom's OUTPUT instead, which is what is wanted.
        this.cutoutGlowBleedContainer = new PIXI.Container();
        this.cutoutGlowBleedContainer.filters = [this.cutoutGlowBleedBloomFilter];

        this.cutoutGlowClipContainer = new PIXI.Container();
        this.cutoutGlowClipContainer.mask = this.cutoutGlowMaskGraphic;
        this.cutoutGlowClipContainer.addChild(this.cutoutGlowBleedContainer);

        // A display object holds a single mask, so the transparent center keeps its own wrapper.
        this.cutoutGlowContainer = new PIXI.Container();
        this.cutoutGlowContainer.visible = false;
        this.cutoutGlowContainer.addChild(this.cutoutGlowClipContainer);

        // Added after every layer container so the bleed sits above their ERASE graphics and survives
        // the knockout.
        this.visualsContainer.addChild(this.cutoutGlowContainer);

        this.cutoutGlowBleedActive = false;
    }

    // The glow a layer actually renders with: its own when Customize carries an enabled glow, and the
    // global one otherwise. Null when the layer does not glow at all.
    resolveLayerGlow(settings) {
        const useCustom = settings.customOptions && settings.customOptions.enabled;
        const customGlow = useCustom ? settings.customOptions.glowSettings : null;

        if (customGlow && customGlow.enabled)
            return customGlow;

        return this.config.glowSettings.enabled ? this.config.glowSettings : null;
    }

    // A layer's glow is drawn INSIDE the layer stack when it is per-layer: its own custom glow, or the
    // global one routed per-layer so the clone picks up that layer's fire/shadow distortion. Inside the
    // stack a cutout in front still knocks it out, and the shared glow's route around the knockout does
    // not help it. Those layers are the only ones that still need the bleed pass.
    usesPerLayerGlow(settings) {
        const useCustom = settings.customOptions && settings.customOptions.enabled;
        const hasCustomGlow = useCustom && settings.customOptions.glowSettings && settings.customOptions.glowSettings.enabled;
        const hasCustomFire = useCustom && settings.customOptions.fireSettings && settings.customOptions.fireSettings.enabled;
        const hasCustomShadow = useCustom && settings.customOptions.dropShadowSettings && settings.customOptions.dropShadowSettings.enabled;

        return Boolean(hasCustomGlow || (this.config.glowSettings.enabled && (hasCustomFire || hasCustomShadow)));
    }

    feedsCutoutGlowBleed(settings) {
        return Boolean(settings.visible && !settings.customOptions?.cutout && this.usesPerLayerGlow(settings));
    }

    // One bloom pass serves the whole hole, so it needs a single blur and scale. The pass now exists
    // only for per-layer glows, so its parameters come from the first layer feeding it.
    resolveCutoutGlowBleedGlow() {
        const contributor = this.waveSettings.find((settings) => this.feedsCutoutGlowBleed(settings));
        return contributor ? this.resolveLayerGlow(contributor) : null;
    }

    // Decided before the layer loop so the knockout painters and applyGlowEffect both know whether to
    // feed the bleed pass while they walk the layers.
    prepareCutoutGlowBleed() {
        // The shared glow no longer needs this pass: it sits outside the isolated layer stack and its
        // source carries the knockouts, so it already spills into the hole on its own. Running the bleed
        // for it as well would light the hole twice. Per-layer glow is a different case, and for it the
        // pass is still the only thing putting light back.
        const hasVisibleCutout = this.waveSettings.some((settings) => settings.visible && settings.customOptions?.cutout);

        this.cutoutGlowBleedActive = hasVisibleCutout
            && this.waveSettings.some((settings) => this.feedsCutoutGlowBleed(settings));

        this.cutoutGlowContainer.visible = this.cutoutGlowBleedActive;
        this.cutoutGlowBleedContainer.removeChildren();
        this.cutoutGlowEraseGraphic.clear();
        this.cutoutGlowMaskGraphic.clear();
    }

    finishCutoutGlowBleed() {
        // Re-attached every frame because removeChildren() above detaches it with the clones. It has to
        // be the last child so it knocks the hole out of the whole glow source the bloom is taken from.
        // Re-attached even when the bleed is idle, so the graphic always has an owner to destroy it.
        this.cutoutGlowBleedContainer.addChild(this.cutoutGlowEraseGraphic);
    }

    getProcessedSpectrum(spectrums, waveType, frameDelay, spectrumOptions) {
        const targetSpectrum = spectrums[waveType === WAVE_TYPE.BASS_SPECTRUM ? "bass" : "wide"];
        return ProcessSpectrum(targetSpectrum[frameDelay], spectrumOptions);
    }

    applyBounceEffect(spectrumMagnitude, waveType) {
        const bounceScale = waveType === WAVE_TYPE.BASS_SPECTRUM ? this.config.bassBounceScale : this.config.wideBounceScale;

        if (this.config.bounceEnabled) {
            const newScale = this.scaleAmount * (1 + (spectrumMagnitude / 255) * bounceScale);
            this.logoContainer.scale.set(newScale, newScale);
            this.visualsContainer.scale.set(newScale, newScale);
        }
    }

    applyGlowEffect(waveCircle, effects, layerGlowSettings, settings) {
        const glowContainer = effects.glowContainer;
        const useCustom = settings.customOptions && settings.customOptions.enabled;
        const hasCustomGlow = useCustom && layerGlowSettings && layerGlowSettings.enabled;
        const hasCustomFire = useCustom && settings.customOptions?.fireSettings && settings.customOptions.fireSettings.enabled;
        const hasCustomShadow = useCustom && settings.customOptions?.dropShadowSettings && settings.customOptions.dropShadowSettings.enabled;
        if (hasCustomGlow || (this.config.glowSettings.enabled && (hasCustomFire || hasCustomShadow))) {
            // PER-LAYER GLOW:
            // Use the per-layer glowContainer when:
            // 1. The layer has an explicit custom glow, OR
            // 2. Global glow is active but the layer has custom fire or shadow.
            //    In case (2), putting the clone in the sharedGlowContainer would render
            //    without fire/shadow distortion (shared fire/shadow only activates for global),
            //    creating a visible "ghost" layer underneath. By routing to the per-layer
            //    glowContainer, the clone inherits the layer's own displacement filter.
            const clone = waveCircle.clone();
            clone.rotation = waveCircle.rotation;
            clone.scale.set(waveCircle.scale.x, waveCircle.scale.y);
            glowContainer.addChild(clone);
        }

        // GLOBAL GLOW:
        // The clone goes into this layer's own slot of the shared glow source, keeping the stack's
        // z-order, so the knockouts inside that source cut the same layers they cut on screen. One bloom
        // filter still processes the whole source, so layer colours mix into a single halo.
        // The shared source feeds the global shadow too, and an empty source has nothing to cast one
        // from, so a global shadow fills it even when the global glow is off.
        const avoidsGhostLayer = !(hasCustomFire || hasCustomShadow);
        const feedsSharedSource = this.config.glowSettings.enabled || this.config.dropShadowSettings.enabled;
        if (feedsSharedSource && avoidsGhostLayer) {
            const clone = waveCircle.clone();
            clone.rotation = waveCircle.rotation;
            clone.scale.set(waveCircle.scale.x, waveCircle.scale.y);
            effects.glowWaveHolder.addChild(clone);
        }

        // CUTOUT GLOW BLEED:
        // Only per-layer glows still need it. Their light is drawn inside the isolated layer stack, so a
        // cutout in front knocks it out and the shared glow's route around the knockout cannot help them.
        // A clone can only have one parent, so the pass gets its own copy.
        if (this.cutoutGlowBleedActive && this.feedsCutoutGlowBleed(settings)) {
            const bleedClone = waveCircle.clone();
            bleedClone.rotation = waveCircle.rotation;
            bleedClone.scale.set(waveCircle.scale.x, waveCircle.scale.y);
            this.cutoutGlowBleedContainer.addChild(bleedClone);
        }
    }

    /**
    * Main function to update the transform
    */
    updateTransform(spectrumMagnitude, bassSpectrumMagnitude, waveType) {
        // shake and drift effects never run simultaneously
        if (this.driftEffect.enabled) {
            this.applyDriftEffect(bassSpectrumMagnitude);
        } else if (this.config.shakeEnabled) {
            this.applyShakeEffect(spectrumMagnitude, waveType);
        } else {
            // No shake/drift active. Reset translation & rotation to base, but keep the
            // scale applied by applyBounceEffect so bounce still works when shake is off.
            const scale = this.config.bounceEnabled ? this.visualsContainer.scale.x : this.scaleAmount;
            this.applyTransform(0, 0, scale, this.baseRotation);
        }
    }

    applyTransform(x, y, scale, rotation) {
        const preserveVisualSpin = this.spinEnabled && !this.driftEffect.enabled;
        const preserveLogoSpin = preserveVisualSpin && !this.spinLogoLocked;
        const visualRotation = preserveVisualSpin ? this.visualsContainer.rotation : rotation;
        const logoRotation = preserveLogoSpin ? this.logoContainer.rotation : rotation;

        this.logoContainer.x = this.x + x;
        this.logoContainer.y = this.y + y;
        this.logoContainer.scale.set(scale, scale);
        this.logoContainer.rotation = Math.abs(logoRotation - rotation) < EPSILON ? rotation : logoRotation;

        this.visualsContainer.x = this.x + x;
        this.visualsContainer.y = this.y + y;
        this.visualsContainer.scale.set(scale, scale);
        this.visualsContainer.rotation = Math.abs(visualRotation - rotation) < EPSILON ? rotation : visualRotation;
    }

    applyDriftEffect(spectrumMagnitude) {
        const transform = this.driftEffect.compute(spectrumMagnitude);

        if (!transform)
            return;

        this.driftTransform = {
            ...transform,
            // Circular visuals make the same angular delta feel weaker than on text/media.
            // Apply a local gain so WaveCircle drift rotation is perceptually comparable.
            rotation: transform.rotation * WAVE_CIRCLE_DRIFT_ROTATION_GAIN
        };

        this.applyTransform(
            transform.translateX,
            transform.translateY,
            this.scaleAmount * transform.scale,
            this.baseRotation + transform.rotation
        );
    }

    applyShakeEffect(spectrumMagnitude, waveType) {
        const shakeFactor = waveType === WAVE_TYPE.BASS_SPECTRUM ? this.config.bassShakeFactor : this.config.wideShakeFactor;

        if (this.config.shakeEnabled) {
            const newXOffset = (this.offset.x + (Math.random() - 0.5) * shakeFactor) * (spectrumMagnitude / 255);
            const newYOffset = (this.offset.y + (Math.random() - 0.5) * shakeFactor) * (spectrumMagnitude / 255);

            this.offset = {
                x: newXOffset,
                y: newYOffset
            };

            this.logoContainer.x = this.x + newXOffset;
            this.logoContainer.y = this.y + newYOffset;

            this.visualsContainer.x = this.x + newXOffset;
            this.visualsContainer.y = this.y + newYOffset;
        }
    }

    draw(spectrums, bassSpectrums, isPlaying) {
        // Note: the `spectrums` object contains arrays of historical frequency data:
        // spectrums.bass: An array for bass layers (typically 7 elements: current + 6 previous)
        // spectrums.wide: An array for "wide" layers (typically 7 elements: current + 6 previous)

        // Guard: skip drawing if PIXI objects are out of sync with settings (e.g. after layer deletion)
        if (!this.waveCircles || this.waveCircles.length !== this.waveSettings.length)
            return;

        // Only the clones are cleared: each layer's glow slot and its knockout graphic are permanent, so
        // the glow source keeps mirroring the layer stack's z-order.
        this.layerEffects.forEach((effects) => effects.glowWaveHolder.removeChildren());

        this.prepareCutoutGlowBleed();

        // Per-layer ERASE graphics are cleared per layer below; this only tracks whether any cutout
        // is active this frame (to toggle the isolating filter).
        let hasCutout = false;

        this.waveCircles.forEach((waveCircle, index) => {
            const settings = this.waveSettings[index];
            const effects = this.layerEffects[index];

            // Clear per-layer glow effects before drawing new ones
            effects.glowContainer.removeChildren();

            // Clear this layer's ERASE graphics each frame so a toggled-off cutout leaves no stale hole,
            // in the layer stack and in the glow source alike.
            effects.cutoutEraseTargets.forEach((eraseGraphic) => eraseGraphic.clear());

            const {
                waveStyle,
                waveType,
                pointRadius,
                barWidth,
                inverted,
                reflection,
                rotation,
                radius,
                inwards,
                hollowCenter,
                cutout
            } = this.getVisualizationOptions(settings);

            // Calculate the processed spectrum based on settings
            const processedSpectrum = this.getProcessedSpectrum(
                spectrums,
                waveType,
                settings.frameDelay,
                settings.spectrumOptions
            );

            if (!settings.visible) {
                waveCircle.clear();
                return;
            }

            const useCustom = settings.customOptions && settings.customOptions.enabled;
            const spinSettings = useCustom && settings.customOptions?.spinSettings ? settings.customOptions.spinSettings : { enabled: false, speed: 0, acceleration: 0 };
            const calculatedRotation = this.adjustLayerRotation(bassSpectrums, spinSettings, index);
            const targetMax = waveType === WAVE_TYPE.BASS_SPECTRUM
                ? CONSTANTS.BASS_TARGET_MAX
                : CONSTANTS.WIDE_TARGET_MAX;

            if (waveStyle === WAVE_STYLE.SOLID || waveStyle === WAVE_STYLE.LINE) {
                // Solid fills the whole disc, so Hollow Center turns it into a band that starts at the
                // ring. Line is already an outline and covers no center, so it ignores the setting.
                const hollowSolid = hollowCenter && waveStyle === WAVE_STYLE.SOLID;
                const { points, magnitudePercents, basePoints } = this.spectrumToPoints(
                    processedSpectrum,
                    radius,
                    reflection,
                    inverted,
                    settings.waveScale,
                    targetMax,
                    inwards,
                    hollowSolid
                );
                const hollowOptions = hollowSolid ? { basePoints, inwards } : null;
                if (cutout) {
                    if (waveStyle === WAVE_STYLE.LINE)
                        this.drawCutoutEraseLine(effects.cutoutEraseTargets, waveCircle, settings, points, calculatedRotation, rotation);
                    else
                        this.drawCutoutErase(effects.cutoutEraseTargets, waveCircle, settings, points, calculatedRotation, rotation, hollowOptions);
                    hasCutout = true;
                } else {
                    // Line is Solid drawn as outline only (no fill), so reuse the same path.
                    const strokeOnly = waveStyle === WAVE_STYLE.LINE;
                    this.drawWaveCircle(waveCircle, settings, points, magnitudePercents, calculatedRotation, rotation, strokeOnly, hollowOptions);
                }
            } else if (waveStyle === WAVE_STYLE.BAR) {
                const { pointSets, magnitudePercents } = this.spectrumToBarPoints(
                    processedSpectrum,
                    radius,
                    reflection,
                    inverted,
                    settings.waveScale,
                    barWidth,
                    targetMax,
                    inwards,
                    hollowCenter
                );
                if (cutout) {
                    this.drawCutoutEraseBars(effects.cutoutEraseTargets, waveCircle, settings, pointSets, calculatedRotation, rotation);
                    hasCutout = true;
                } else {
                    this.drawWaveCircleBars(waveCircle, settings, pointSets, magnitudePercents, calculatedRotation, rotation);
                }
            } else if (waveStyle === WAVE_STYLE.POINT) {
                const { points, magnitudePercents } = this.spectrumToPoints(
                    processedSpectrum,
                    radius,
                    reflection,
                    inverted,
                    settings.waveScale,
                    targetMax,
                    inwards
                );
                if (cutout) {
                    this.drawCutoutErasePoints(effects.cutoutEraseTargets, waveCircle, settings, points, pointRadius, calculatedRotation, rotation);
                    hasCutout = true;
                } else {
                    this.drawWaveCirclePoints(waveCircle, settings, points, pointRadius, magnitudePercents, calculatedRotation, rotation);
                }
            }
            // A cutout layer is invisible (only a knockout hole), so it never contributes glow.
            if (!cutout)
                this.applyGlowEffect(waveCircle, effects, settings.customOptions?.glowSettings, settings);
        });

        // Isolate the visuals container only while a cutout is active, so the ERASE blend knocks out
        // the visualizer onto its own texture (revealing the background) instead of erasing the
        // already-drawn background in the main framebuffer. Toggle only on change to avoid churn.
        const isolated = this.visualsContainer.filters != null;
        if (hasCutout && !isolated)
            this.visualsContainer.filters = [this.cutoutIsolationFilter];
        else if (!hasCutout && isolated)
            this.visualsContainer.filters = null;

        // The layer stack gets its own framebuffer on the same condition, so a knockout stops at the
        // bottom of the stack and leaves the glow beneath it intact. Without this the ERASE would carry
        // on through the shared framebuffer and take the glow with it, which is what used to make a hole
        // read as a dead black area instead of a hole with light spilling into it.
        const stackIsolated = this.layerStackContainer.filters != null;
        if (hasCutout && !stackIsolated)
            this.layerStackContainer.filters = [this.layerStackIsolationFilter];
        else if (!hasCutout && stackIsolated)
            this.layerStackContainer.filters = null;

        this.finishCutoutGlowBleed();

        const bassSpectrumMagnitude = GetSpectrumMagnitude(bassSpectrums);

        // Global reactivity (bounce/shake) follows the GLOBAL spectrum setting, not the last layer's
        // per-layer spectrum, so changing one layer's spectrum no longer alters the whole visualizer.
        // Drift keeps its own bass magnitude (updateTransform's second argument).
        const globalWaveType = this.config.waveType;
        const globalSpectrumFrame = spectrums[globalWaveType === WAVE_TYPE.BASS_SPECTRUM ? "bass" : "wide"][0];
        const globalSpectrumMagnitude = GetSpectrumMagnitude(globalSpectrumFrame);
        this.applyBounceEffect(globalSpectrumMagnitude, globalWaveType);
        this.updateTransform(globalSpectrumMagnitude, bassSpectrumMagnitude, globalWaveType);

        // No need to spin the visualized when preview is paused.
        if (this.spinEnabled && isPlaying)
            this.drawSpin(bassSpectrums);

        // Update per-layer fire animation
        this.layerEffects.forEach((effects, index) => {
            const settings = this.waveSettings[index];
            const useCustom = settings.customOptions && settings.customOptions.enabled;
            const fire = useCustom && settings.customOptions?.fireSettings && settings.customOptions.fireSettings.enabled ? settings.customOptions.fireSettings : this.config.fireSettings;
            if (fire && fire.enabled && effects.displacementSprite) {
                effects.displacementSprite.y -= fireSpeed;
            }
        });

        // Update shared fire animation for global glow
        if (this.sharedDisplacementSprite && this.config.fireSettings.enabled) {
            this.sharedDisplacementSprite.y -= fireSpeed;
        }
    }

    setRotation(graphic, settings, calculatedRotation, rotation) {
        const useCustom = settings.customOptions && settings.customOptions.enabled;
        const spinSettings = useCustom && settings.customOptions?.spinSettings ? settings.customOptions.spinSettings : { enabled: false };
        if (spinSettings.enabled) {
            graphic.rotation += calculatedRotation;
        } else {
            graphic.rotation = rotation;
        }
    }

    // hollowOptions is null unless Hollow Center applies: { basePoints, inwards }, the ring the waves
    // start from plus the direction that says which side of it the band lies on.
    drawWaveCircle(graphic, settings, points, magnitudePercents, calculatedRotation, rotation, strokeOnly, hollowOptions) {
        let mixPercent = average(magnitudePercents) * 5;
        mixPercent = mixPercent > 1 ? 1 : mixPercent;

        graphic.clear();
        this.setRotation(graphic, settings, calculatedRotation, rotation);

        let color = settings.fillColor;
        let alpha = settings.fillAlpha;
        if (settings.secondaryFillColor) {
            color = (color === '0x000000' ? '0x010101' : color).replace('0x', '#');
            let secondaryColor = (settings.secondaryFillColor === '0x000000' ? '0x010101' : settings.secondaryFillColor).replace('0x', '#');
            color = chroma.mix(color, secondaryColor, mixPercent, 'lch').hex().replace('#', '0x');
            alpha = settings.fillAlpha * (1 - mixPercent) + settings.secondaryFillAlpha * mixPercent;
        }
        let lineColor = settings.lineColor;
        let lineAlpha = settings.lineAlpha;
        if (settings.secondaryLineColor) {
            lineColor = (lineColor === '0x000000' ? '0x010101' : lineColor).replace('0x', '#');
            let secondaryLineColor = (settings.secondaryLineColor === '0x000000' ? '0x010101' : settings.secondaryLineColor).replace('0x', '#');
            lineColor = chroma.mix(lineColor, secondaryLineColor, mixPercent, 'lch').hex().replace('#', '0x');
            lineAlpha = settings.lineAlpha * (1 - mixPercent) + settings.secondaryLineAlpha * mixPercent;
        }

        if (hollowOptions) {
            this.drawHollowWaveCircle(graphic, settings, points, hollowOptions, color, alpha, lineColor, lineAlpha);
            return;
        }

        graphic.lineStyle(settings.lineWidth, lineColor, lineAlpha);
        graphic.beginFill(color, strokeOnly ? 0 : alpha);
        graphic.drawPolygon(points);
        graphic.endFill();
    }

    // Hollow Center: the fill covers only the band between the ring and the wave contour, so the inner
    // loop is punched out as a hole. The outline is stroked afterwards as two separate closed loops,
    // because a hole carries no line style of its own.
    drawHollowWaveCircle(graphic, settings, points, hollowOptions, color, alpha, lineColor, lineAlpha) {
        const { basePoints, inwards } = hollowOptions;
        const { outerLoop, innerLoop } = orderHollowLoops(points, basePoints, inwards);

        graphic.lineStyle(0);
        graphic.beginFill(color, alpha);
        graphic.drawPolygon(outerLoop);
        graphic.beginHole();
        graphic.drawPolygon(innerLoop);
        graphic.endHole();
        graphic.endFill();

        if (settings.lineWidth > 0) {
            graphic.lineStyle(settings.lineWidth, lineColor, lineAlpha);
            graphic.drawPolygon(points);
            graphic.drawPolygon(basePoints);
        }
    }

    drawWaveCirclePoints(graphic, settings, points, radius, magnitudePercents, calculatedRotation, rotation) {
        graphic.clear();
        this.setRotation(graphic, settings, calculatedRotation, rotation);

        for (let i = 0; i < points.length - 1; i += 2) {
            let color = settings.fillColor;
            let alpha = settings.fillAlpha;
            if (settings.secondaryFillColor) {
                color = (color === '0x000000' ? '0x010101' : color).replace('0x', '#');
                let secondaryColor = (settings.secondaryFillColor === '0x000000' ? '0x010101' : settings.secondaryFillColor).replace('0x', '#');
                color = chroma.mix(color, secondaryColor, magnitudePercents[i / 2], 'lch').hex().replace('#', '0x');
                alpha = settings.fillAlpha * (1 - magnitudePercents[i / 2]) + settings.secondaryFillAlpha * magnitudePercents[i / 2];
            }
            let lineColor = settings.lineColor;
            let lineAlpha = settings.lineAlpha;
            if (settings.secondaryLineColor) {
                lineColor = (lineColor === '0x000000' ? '0x010101' : lineColor).replace('0x', '#');
                let secondaryLineColor = (settings.secondaryLineColor === '0x000000' ? '0x010101' : settings.secondaryLineColor).replace('0x', '#');
                lineColor = chroma.mix(lineColor, secondaryLineColor, magnitudePercents[i / 2], 'lch').hex().replace('#', '0x');
                lineAlpha = settings.lineAlpha * (1 - magnitudePercents[i / 2]) + settings.secondaryLineAlpha * magnitudePercents[i / 2];
            }

            graphic.lineStyle(settings.lineWidth, lineColor, lineAlpha);
            graphic.beginFill(color, alpha);
            graphic.drawCircle(points[i], points[i + 1], radius);
            graphic.endFill();
        }

    }

    drawWaveCircleBars(graphic, settings, points, magnitudePercents, calculatedRotation, rotation) {
        graphic.clear();
        this.setRotation(graphic, settings, calculatedRotation, rotation);

        for (let i = 0; i < points.length; i++) {
            let color = settings.fillColor;
            let alpha = settings.fillAlpha;
            if (settings.secondaryFillColor) {
                color = (color === '0x000000' ? '0x010101' : color).replace('0x', '#');
                let secondaryColor = (settings.secondaryFillColor === '0x000000' ? '0x010101' : settings.secondaryFillColor).replace('0x', '#');
                color = chroma.mix(color, secondaryColor, magnitudePercents[i], 'lch').hex().replace('#', '0x');
                alpha = settings.fillAlpha * (1 - magnitudePercents[i]) + settings.secondaryFillAlpha * magnitudePercents[i];
            }
            let lineColor = settings.lineColor;
            let lineAlpha = settings.lineAlpha;
            if (settings.secondaryLineColor) {
                lineColor = (lineColor === '0x000000' ? '0x010101' : lineColor).replace('0x', '#');
                let secondaryLineColor = (settings.secondaryLineColor === '0x000000' ? '0x010101' : settings.secondaryLineColor).replace('0x', '#');
                lineColor = chroma.mix(lineColor, secondaryLineColor, magnitudePercents[i], 'lch').hex().replace('#', '0x');
                lineAlpha = settings.lineAlpha * (1 - magnitudePercents[i]) + settings.secondaryLineAlpha * magnitudePercents[i];
            }

            graphic.lineStyle(settings.lineWidth, lineColor, lineAlpha);
            graphic.beginFill(color, alpha);
            graphic.drawPolygon(points[i]);
            graphic.endFill();
        }
    }

    calculateRotation(spinSettings, bassSpectrums, lastFrameTimestamp) {
        const { enabled, speed, acceleration } = spinSettings;

        if (!enabled) {
            return { radiansRotation: 0, currentTime: performance.now() };
        }

        let currentTime = performance.now();

        // The result value will be in milliseconds. For example: 30 fps, 1000 / 30 = 33,33 milliseconds per frame.
        // The fps on UI is not stable. Thats why we need to calculate it for each frame.
        let frameTime = currentTime - (lastFrameTimestamp || 0);

        // The condition to prevent the initial or old values in lastFrameTimeStamp.
        // Just set the default frame time for 30fps.
        if (!frameTime || frameTime > 1000) {
            frameTime = (1000 / 30);
        }

        // Calculate the radians we need to rotate for static speed and current frame.
        let radiansRotation = ((speed * (Math.PI * 2) / 60) * frameTime) / 1000;

        // Calculate the acceleration.
        let magnitude = GetSpectrumMagnitude(bassSpectrums);
        let radiansAcceleration = ((acceleration * (Math.PI * 2) / 60) * frameTime) / 1000;
        let multiplier = magnitude / 2;
        radiansRotation += radiansAcceleration * multiplier;

        return { radiansRotation, currentTime };
    }

    adjustLayerRotation(bassSpectrums, spinSettings, index) {
        const lastFrameTimestampKey = `lastFrameTimeStamp_${index}`;
        const rotationInfo = this.calculateRotation(spinSettings, bassSpectrums, this[lastFrameTimestampKey]);
        this[lastFrameTimestampKey] = rotationInfo.currentTime;

        return rotationInfo.radiansRotation;
    }

    drawSpin(bassSpectrums) {
        const rotationInfo = this.calculateRotation(this.spinSettings, bassSpectrums, this.lastFrameTimeStamp);
        const radiansRotation = rotationInfo.radiansRotation;

        this.visualsContainer.rotation += radiansRotation;

        if (!this.spinLogoLocked) {
            this.logoContainer.rotation += radiansRotation;
        }

        this.lastFrameTimeStamp = rotationInfo.currentTime;
    }

    indexToPoints(i, length, reflection, radius, magnitude, inwards) {
        let points = [];
        let t = 0;

        switch (reflection) {
            case REFLECTION_TYPE.NONE:
                t = 2 * (Math.PI * (i / (length)) - Math.PI / 4);
                break;
            case REFLECTION_TYPE.VERTICAL:
                t = Math.PI * ((i + 0.5) / (length)) - Math.PI / 2;
                break;
            case REFLECTION_TYPE.SLANTED:
                t = Math.PI * (i / (length)) - Math.PI / 4;
                break;
            case REFLECTION_TYPE.THREE_WAY:
                t = 0.66666666 * (Math.PI * (i / (length)) - Math.PI / 4);
                break;
            case REFLECTION_TYPE.FOUR_WAY:
                t = (Math.PI / 1.9999) * ((i + 0.5) / (length)) - Math.PI / 2;
                break;
            default:
                break;
        }
        // Inward peaks are capped short of the center: reaching it collapses vertices onto (0,0) and
        // self-intersects the polygon into spikes, so keep a small minimum inner radius. The inward
        // amplitude is therefore never allowed to exceed the ring radius (waves stay inside the ring).
        let r = inwards
            ? Math.max(radius * INWARD_MIN_RADIUS_RATIO, radius - magnitude)
            : radius + magnitude;
        points.push(-r * Math.cos(t));
        points.push(r * Math.sin(t));

        switch (reflection) {
            case REFLECTION_TYPE.NONE:
                break;
            case REFLECTION_TYPE.VERTICAL:
                points.push(r * Math.cos(t));
                points.push(r * Math.sin(t));
                break;
            case REFLECTION_TYPE.SLANTED:
                points.push(r * Math.cos(t));
                points.push(-r * Math.sin(t));
                break;
            case REFLECTION_TYPE.THREE_WAY:
                t = 0.66666666 * (Math.PI * ((i + length) / (length)) - Math.PI / 4);
                points.push(-r * Math.cos(t));
                points.push(r * Math.sin(t));

                t = 0.66666666 * (Math.PI * ((i + length * 2) / (length)) - Math.PI / 4);
                points.push(-r * Math.cos(t));
                points.push(r * Math.sin(t));

                break;
            case REFLECTION_TYPE.FOUR_WAY:
                points.push(r * Math.cos(t));
                points.push(r * Math.sin(t));

                const invT = (Math.PI / 1.9999) * ((length - 1 - i + 0.5) / (length)) - Math.PI / 2;

                points.push(r * Math.cos(invT));
                points.push(-r * Math.sin(invT));

                points.push(-r * Math.cos(invT));
                points.push(-r * Math.sin(invT));
                break;
            default:
                break;
        }

        return points;
    }

    concatPoints(points, pointSets, pointSetIndex, magnitudePercents) {
        for (let i = 0; i < pointSets.length; i++) {
            points.push(pointSets[i][pointSetIndex]);
            points.push(pointSets[i][pointSetIndex + 1]);
            magnitudePercents.push(pointSets[i][pointSets[i].length - 1]);
        }
    }

    // Walks the per-index point collections in the order the reflection lays them out, producing one
    // closed loop around the center. Both the wave contour and the ring it is anchored to are assembled
    // with this, so their vertices stay index-for-index aligned.
    assembleLoopPoints(collectedPoints, reflection, points, magnitudePercents) {
        this.concatPoints(points, collectedPoints, 0, magnitudePercents);

        switch (reflection) {
            case REFLECTION_TYPE.NONE:
                break;
            case REFLECTION_TYPE.VERTICAL:
                this.concatPoints(points, collectedPoints.slice().reverse(), 2, magnitudePercents);
                break;
            case REFLECTION_TYPE.SLANTED:
                this.concatPoints(points, collectedPoints, 2, magnitudePercents);
                break;
            case REFLECTION_TYPE.THREE_WAY:
                this.concatPoints(points, collectedPoints, 2, magnitudePercents);
                this.concatPoints(points, collectedPoints, 4, magnitudePercents);
                break;
            case REFLECTION_TYPE.FOUR_WAY:
                const reversedCollectedPoints = collectedPoints.slice().reverse();
                this.concatPoints(points, collectedPoints, 6, magnitudePercents);
                this.concatPoints(points, reversedCollectedPoints, 4, magnitudePercents);
                this.concatPoints(points, reversedCollectedPoints, 2, magnitudePercents);
                break;
            default:
                break;
        }
    }

    // With hollowCenter the caller also gets basePoints: the same loop taken at zero magnitude, i.e. the
    // ring the waves start from. Filling between the two loops leaves the center empty.
    spectrumToPoints(spectrum, radius, reflection, inverted, waveScale, targetMax, inwards, hollowCenter) {
        let points = [];
        let magnitudePercents = [];
        let length = spectrum.length;

        let newSpectrum = inverted ? spectrum.slice().reverse() : spectrum.slice();

        let collectedPoints = [];
        let collectedBasePoints = [];
        for (let i = 0; i < length; i++) {
            const pointCollection = this.indexToPoints(i, length, reflection, radius, newSpectrum[i] * waveScale, inwards);
            let magnitudePercent = newSpectrum[i] / targetMax;
            magnitudePercent = magnitudePercent > 1 ? 1 : magnitudePercent;
            pointCollection.push(magnitudePercent);
            collectedPoints.push(pointCollection);

            if (hollowCenter) {
                const baseCollection = this.indexToPoints(i, length, reflection, radius, 0, inwards);
                baseCollection.push(magnitudePercent);
                collectedBasePoints.push(baseCollection);
            }
        }

        this.assembleLoopPoints(collectedPoints, reflection, points, magnitudePercents);

        if (!hollowCenter)
            return { points: points, magnitudePercents: magnitudePercents };

        const basePoints = [];
        this.assembleLoopPoints(collectedBasePoints, reflection, basePoints, []);

        return { points: points, magnitudePercents: magnitudePercents, basePoints: basePoints };
    }

    spectrumToBarPoints(spectrum, radius, reflection, inverted, waveScale, barWidth, targetMax, inwards, hollowCenter) {
        let pointSets = [];
        let magnitudePercents = [];
        let length = spectrum.length;

        let newSpectrum = inverted ? spectrum.slice().reverse() : spectrum.slice();

        // The bar tip follows the Direction regardless of Hollow Center: an inward tip grows from the
        // ring toward the center (like Solid/Point/Line), an outward tip grows past the ring. Ring
        // anchoring (a quad from the ring to the tip) stays tied to Hollow Center only; without it the
        // bar is a filled center wedge, so its fill is kept while the tip moves inward.
        for (let i = 0; i < length; i++) {
            const magnitude = newSpectrum[i] * waveScale;
            const tipSet1 = this.indexToPoints(i - barWidth / 2, length, reflection, radius, magnitude, inwards);
            const tipSet2 = this.indexToPoints(i + barWidth / 2, length, reflection, radius, magnitude, inwards);

            // Hollow-center bars are anchored on the ring and span from it (zero magnitude) to the tip,
            // so each bar is a quad. Otherwise the bar keeps the wedge that radiates from the center to
            // the tip (a filled triangle), which stays filled while the tip follows the direction.
            const baseSet1 = hollowCenter ? this.indexToPoints(i - barWidth / 2, length, reflection, radius, 0, inwards) : null;
            const baseSet2 = hollowCenter ? this.indexToPoints(i + barWidth / 2, length, reflection, radius, 0, inwards) : null;

            const buildBarPolygon = (pairIndex) => {
                const x = pairIndex * 2;
                const y = x + 1;
                if (hollowCenter) {
                    return [baseSet1[x], baseSet1[y], tipSet1[x], tipSet1[y], tipSet2[x], tipSet2[y], baseSet2[x], baseSet2[y]];
                }
                return [0, 0, tipSet1[x], tipSet1[y], tipSet2[x], tipSet2[y]];
            };

            let magnitudePercent = newSpectrum[i] / targetMax;
            magnitudePercent = magnitudePercent > 1 ? 1 : magnitudePercent;

            const reflectionCopyCount = {
                [REFLECTION_TYPE.NONE]: 1,
                [REFLECTION_TYPE.VERTICAL]: 2,
                [REFLECTION_TYPE.SLANTED]: 2,
                [REFLECTION_TYPE.THREE_WAY]: 3,
                [REFLECTION_TYPE.FOUR_WAY]: 4
            }[reflection] || 1;

            for (let pairIndex = 0; pairIndex < reflectionCopyCount; pairIndex++) {
                pointSets.push(buildBarPolygon(pairIndex));
                magnitudePercents.push(magnitudePercent);
            }
        }

        return { pointSets: pointSets, magnitudePercents: magnitudePercents };
    }



    setPositionAndScale(multiplier) {
        this.scaleAmount = multiplier * (this.config.radiusFactor / baseRadius);

        this.logoContainer.scale.set(this.scaleAmount, this.scaleAmount);
        this.visualsContainer.scale.set(this.scaleAmount, this.scaleAmount);

        this.moveTo(this.config.position);
    }

    // Separate from setPositionAndScale so a canvas drag can move the visualizer on its own, without a
    // round trip through the store, and still land on the same pixel the stored position means.
    //
    // Both containers are placed, because the logo rides along with the waves. Bounce, shake and drift
    // all offset from this.x and this.y rather than replacing them, so they keep working from the new
    // place without being told anything.
    moveTo(position) {
        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;

        this.x = CONSTANTS.DEFAULT_WIDTH * 0.5 + (position.x * multiplier);
        this.y = CONSTANTS.DEFAULT_HEIGHT * 0.5 + (position.y * multiplier);

        this.logoContainer.x = this.x;
        this.logoContainer.y = this.y;

        this.visualsContainer.x = this.x;
        this.visualsContainer.y = this.y;
    }

    updateStage(stage) {
        this.stage = stage;
        this.stage.addChild(this.logoContainer);
        this.stage.addChild(this.visualsContainer);
    }

    resetSpinPosition() {
        const rotation = this.config.rotation / (180 / Math.PI);
        this.lastFrameTimeStamp = undefined;
        this.logoContainer.rotation = rotation;
        this.visualsContainer.rotation = rotation;
    }

    loadImageTexture = (config) => {
        if (config.mediaSource && this.currentMediaSourceRef !== config.mediaSource) {
            this.logoSprite.texture.destroy();
            this.logoSprite.texture = PIXI.Texture.from(config.mediaSource.data.url);
            this.logoGlowSprite.texture = this.logoSprite.texture;
            this.currentMediaSourceRef = config.mediaSource;
        }
    }

    initializeLayer(waveCircles) {
        this.waveCircles = [];
        this.layerEffects = [];
        this.visualsContainer.removeChildren();

        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;

        // SHARED GLOW CONTAINER:
        // We create a shared container for global glow *before* adding individual layer containers.
        // Why? In the original architecture, all layer clones went into one shared container
        // with one bloom filter. This created a combined glow where layer colors mix naturally.
        // If we put global glow on each layer individually, their blooms don't mix correctly and
        // the glow appears as an "inner" glow because it gets trapped under each wave layer.
        this.sharedGlowContainer = new PIXI.Container();
        this.sharedGlowContainer.filters = [];
        this.visualsContainer.addChild(this.sharedGlowContainer);

        // Every layer container lives in here rather than directly in visualsContainer, so the stack
        // renders into its own framebuffer and a cutout's ERASE cannot reach the glow drawn beneath it.
        this.layerStackContainer = new PIXI.Container();
        this.visualsContainer.addChild(this.layerStackContainer);

        // Add shared bloom filter
        this.sharedBloomFilter = new PIXIFilters.AdvancedBloomFilter({
            threshold: 0.01,
            quality: 10
        });
        this.sharedBloomFilter.padding = 100 * multiplier;
        this.sharedGlowContainer.filters.push(this.sharedBloomFilter);

        // Add shared shadow filters for when global glow + shadow are both active
        const shadowSettings = { quality: 10, distance: 0 };
        this.sharedGlowShadowFilter1 = new PIXIFilters.DropShadowFilter(shadowSettings);
        this.sharedGlowShadowFilter2 = new PIXIFilters.DropShadowFilter(shadowSettings);
        this.sharedGlowContainer.filters.push(this.sharedGlowShadowFilter1);
        this.sharedGlowContainer.filters.push(this.sharedGlowShadowFilter2);

        // Add shared fire displacement filter for global glow + fire combination
        this.sharedDisplacementSprite = new PIXI.Sprite();
        this.sharedDisplacementSprite.texture = PIXI.Texture.from('https://specterr.b-cdn.net/fire-texture.jpg');
        this.sharedDisplacementSprite.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.MIRRORED_REPEAT;
        this.sharedDisplacementFilter = new PIXI.filters.DisplacementFilter(this.sharedDisplacementSprite);
        this.sharedDisplacementSprite.x -= 500;
        this.sharedDisplacementFilter.padding = 110 * multiplier;
        this.sharedDisplacementFilter.enabled = false;
        // Add sprite to visualsContainer (needed for filter to work)
        this.visualsContainer.addChild(this.sharedDisplacementSprite);
        // Insert displacement filter before bloom filter
        this.sharedGlowContainer.filters.unshift(this.sharedDisplacementFilter);

        waveCircles.forEach((settings, index) => this.addNewLayer(settings, index, multiplier));

        this.createCutoutGlowBleed(multiplier);

        // Padding covers the glow extent so the isolating filter does not clip the glow when active.
        // (Per-layer ERASE graphics now live inside each layerContainer; there is no global one.)
        this.cutoutIsolationFilter.padding = 200 * multiplier;
        this.layerStackIsolationFilter.padding = 200 * multiplier;
    }

    addNewLayer(settings, index, multiplier) {
        if (settings.frameDelay > 0)
            this.maxFrameDelay = Math.max(settings.frameDelay, this.maxFrameDelay);

        const layerContainer = new PIXI.Container();
        const waveContainer = new PIXI.Container();
        const glowContainer = new PIXI.Container();

        waveContainer.filters = [];
        glowContainer.filters = [];

        layerContainer.addChild(glowContainer);
        layerContainer.addChild(waveContainer);
        this.layerStackContainer.addChild(layerContainer);

        const waveCircle = new PIXI.Graphics();
        waveCircle.scale.set(settings.scale, settings.scale);
        waveContainer.addChild(waveCircle);

        // The glow source mirrors the layer stack one slot per layer, each with its own knockout at the
        // same z-position. That is what makes the glow come from the shapes as they are actually seen: a
        // cutout removes the layers behind it from the glow too, so the bloom is computed from the
        // visible colour and not from shapes that the hole covers.
        const glowSlotContainer = new PIXI.Container();
        const glowWaveHolder = new PIXI.Container();
        const glowEraseGraphic = new PIXI.Graphics();
        glowEraseGraphic.blendMode = PIXI.BLEND_MODES.ERASE;
        glowSlotContainer.addChild(glowWaveHolder);
        glowSlotContainer.addChild(glowEraseGraphic);
        this.sharedGlowContainer.addChild(glowSlotContainer);

        // Per-layer ERASE knockout for "Transparency Cutout". Attached as the TOP child of this layer's
        // container further below (after the effect-setup calls, which also add children), so it sits at
        // the layer's z-position: in the isolated visuals framebuffer ERASE removes only what was drawn
        // before it (the layers BEHIND this one), while later layers draw on top. Stays empty unless this
        // layer is a cutout (filled per frame in draw()).
        const cutoutEraseGraphic = new PIXI.Graphics();
        cutoutEraseGraphic.blendMode = PIXI.BLEND_MODES.ERASE;

        const effects = {
            layerContainer,
            waveContainer,
            glowContainer,
            cutoutEraseGraphic,
            glowWaveHolder,
            glowEraseGraphic,
            // Every knockout shape is painted into both, so the layer stack and the glow source always
            // agree on where the holes are.
            cutoutEraseTargets: [cutoutEraseGraphic, glowEraseGraphic],
            filters: []
        };

        this.addLayerGlowEffect(effects, multiplier);
        this.addLayerFireEffect(effects, multiplier);
        this.addLayerDropShadowEffect(effects, multiplier);

        // Attach the ERASE graphic LAST so it is the top child of the layer container, above every other
        // child added during effect setup (e.g. the fire displacement sprite). This keeps a cutout's
        // knockout above this layer's own content, preserving a clean hole through the layers behind it.
        layerContainer.addChild(cutoutEraseGraphic);

        this.waveCircles.push(waveCircle);
        this.layerEffects.push(effects);
    }

    addLayerGlowEffect(effects, multiplier) {
        effects.bloomFilter = new PIXIFilters.AdvancedBloomFilter({
            threshold: 0.01,
            quality: 10
        });
        effects.glowContainer.filters.push(effects.bloomFilter);
        effects.bloomFilter.padding = 100 * multiplier;
    }

    addLayerFireEffect(effects, multiplier) {
        effects.displacementSprite = new PIXI.Sprite();
        effects.displacementSprite.texture = PIXI.Texture.from('https://specterr.b-cdn.net/fire-texture.jpg');
        effects.displacementSprite.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.MIRRORED_REPEAT;
        effects.displacementFilter = new PIXI.filters.DisplacementFilter(effects.displacementSprite);
        effects.displacementSprite.x -= 500;
        effects.displacementFilter.padding = 110 * multiplier;

        effects.displacementFilter.enabled = false;

        effects.layerContainer.addChild(effects.displacementSprite);
        effects.glowContainer.filters.push(effects.displacementFilter);
        effects.waveContainer.filters.push(effects.displacementFilter);
    }

    addLayerDropShadowEffect(effects, multiplier) {
        const shadowSettings = {
            quality: 10,
            distance: 0,
        };
        effects.glowShadowFilter1 = new PIXIFilters.DropShadowFilter(shadowSettings);
        effects.glowShadowFilter2 = new PIXIFilters.DropShadowFilter(shadowSettings);
        effects.waveShadowFilter1 = new PIXIFilters.DropShadowFilter(shadowSettings);
        effects.waveShadowFilter2 = new PIXIFilters.DropShadowFilter(shadowSettings);

        effects.waveShadowPaddingFilter = new PIXI.filters.AlphaFilter(1);
        effects.waveShadowPaddingFilter.padding = 100 * multiplier;

        effects.glowContainer.filters.push(effects.glowShadowFilter1);
        effects.glowContainer.filters.push(effects.glowShadowFilter2);
        effects.waveContainer.filters.push(effects.waveShadowFilter1);
        effects.waveContainer.filters.push(effects.waveShadowFilter2);
        effects.waveContainer.filters.push(effects.waveShadowPaddingFilter);
    }

    updateLayerEffects(multiplier) {
        // Guard: skip if config hasn't been populated yet (updateLayers can be called before update)
        if (!this.config.glowSettings || !this.config.dropShadowSettings || !this.config.fireSettings) return;

        const globalGlow = this.config.glowSettings;
        const globalShadow = this.config.dropShadowSettings;

        // Dedicated "Logo Glow" effect (independent Outer/Inner control for the logo).
        // Uses a bloomed clone behind the logo. OUTER keeps the sharp logo on top of a
        // white halo; INNER hides the sharp logo so only the bloomed (colored) version shows.
        if (this.logoGlowBloomFilter && this.logoGlowContainer) {
            const logoGlow = this.config.logoGlowSettings;
            const enabled = !!(logoGlow && logoGlow.enabled);

            this.logoGlowContainer.visible = enabled;
            this.logoGlowBloomFilter.enabled = enabled;

            if (enabled) {
                const inner = isInnerGlow(logoGlow.glowType);
                this.logoGlowBloomFilter.blur = logoGlow.blur * multiplier * LOGO_GLOW_BLUR_SENSITIVITY;
                this.logoGlowBloomFilter.bloomScale = logoGlow.scale * LOGO_GLOW_SCALE_SENSITIVITY;
                this.logoSprite.alpha = inner ? 0 : 1;
            } else {
                this.logoSprite.alpha = 1;
            }
        }

        // Update shared bloom filter (for global glow)
        if (this.sharedBloomFilter) {
            this.sharedBloomFilter.blur = globalGlow.blur * multiplier;
            this.sharedBloomFilter.bloomScale = globalGlow.scale;
            this.sharedBloomFilter.enabled = globalGlow.enabled;
        }

        // The bleed into a transparency cutout is the same light as the layers around the hole, so it
        // follows whichever glow lights them. Its container is hidden outright when there is nothing to
        // bleed into, so the filter stays enabled.
        const cutoutBleedGlow = this.resolveCutoutGlowBleedGlow();
        if (this.cutoutGlowBleedBloomFilter && cutoutBleedGlow) {
            this.cutoutGlowBleedBloomFilter.blur = cutoutBleedGlow.blur * multiplier;
            this.cutoutGlowBleedBloomFilter.bloomScale = cutoutBleedGlow.scale;
        }

        // Update shared shadow filters (for when global glow + shadow are both active)
        if (this.sharedGlowShadowFilter1 && this.sharedGlowShadowFilter2) {
            // The global shadow belongs on the shared source whether or not the global glow is on.
            // Gating it on the glow used to drop every layer into the per-layer branch, and a per-layer
            // shadow renders inside its own layer's node, so the front layer's shadow landed on top of
            // the layers behind it.
            const sharedShadowEnabled = globalShadow.enabled;
            this.sharedGlowShadowFilter1.enabled = sharedShadowEnabled;
            this.sharedGlowShadowFilter2.enabled = sharedShadowEnabled;

            // Without a bloom in front of it the shared source would draw a second, opaque copy of the
            // whole stack underneath the real one: visible through any transparency cutout, and doubling
            // any layer with alpha below 1. shadowOnly drops that copy from the output and keeps the
            // shadow. It goes on the SECOND pass only: the two passes exist to deepen the shadow, which
            // works while the first pass still has the original for the second one to darken.
            this.sharedGlowShadowFilter2.shadowOnly = !globalGlow.enabled;
            this.sharedGlowShadowFilter1.blur = globalShadow.blur * multiplier;
            this.sharedGlowShadowFilter2.blur = globalShadow.blur * multiplier;

            // PixiJS sizes ONE framebuffer for the whole chain, from the largest padding among the
            // ENABLED filters, and DropShadowFilter pads itself to `distance + blur * 2`, enough for a
            // single pass. These two are chained, so the second blurs the output of the first and the
            // shadow travels about twice as far as that. With the glow on, the bloom's own padding was
            // large enough to hide the shortfall; with it off the shadow ended on a straight
            // rectangular edge. Padding is assigned AFTER blur because the blur setter recomputes it.
            const sharedShadowPadding = Math.max(100 * multiplier, globalShadow.blur * multiplier * 4);
            this.sharedGlowShadowFilter1.padding = sharedShadowPadding;
            this.sharedGlowShadowFilter2.padding = sharedShadowPadding;
            this.sharedGlowShadowFilter1.alpha = globalShadow.opacity;
            this.sharedGlowShadowFilter2.alpha = globalShadow.opacity;
            this.sharedGlowShadowFilter1.color = globalShadow.color;
            this.sharedGlowShadowFilter2.color = globalShadow.color;
        }

        // Update shared fire filter for global glow + fire combination
        if (this.sharedDisplacementFilter) {
            const sharedFireEnabled = globalGlow.enabled && this.config.fireSettings.enabled;
            this.sharedDisplacementFilter.enabled = sharedFireEnabled;
            if (sharedFireEnabled) {
                const displacementScale = this.config.fireSettings.intensity * multiplier;
                this.sharedDisplacementFilter.scale = new PIXI.Point(displacementScale, displacementScale * 10);
            }
            if (this.sharedDisplacementSprite) {
                this.sharedDisplacementSprite.scale.y = this.config.fireSettings.detail;
                this.sharedDisplacementSprite.scale.x = this.config.fireSettings.detail;
            }
        }

        this.layerEffects.forEach((effects, index) => {
            const settings = this.waveSettings[index];
            if (!settings) return;
            const useCustom = settings.customOptions && settings.customOptions.enabled;

            // Determine if this layer has custom per-layer glow
            const hasCustomGlow = useCustom && settings.customOptions?.glowSettings && settings.customOptions.glowSettings.enabled;
            const hasCustomFire = useCustom && settings.customOptions?.fireSettings && settings.customOptions.fireSettings.enabled;
            const hasCustomShadow = useCustom && settings.customOptions?.dropShadowSettings && settings.customOptions.dropShadowSettings.enabled;
            const layerGlow = hasCustomGlow ? settings.customOptions.glowSettings : null;

            // Per-layer bloom: enabled for custom per-layer glow, OR when
            // global glow is active and the layer has custom fire or custom shadow.
            const usePerLayerBloom = hasCustomGlow || (globalGlow.enabled && (hasCustomFire || hasCustomShadow));
            if (usePerLayerBloom && effects.bloomFilter) {
                const glowSource = layerGlow || globalGlow;
                effects.bloomFilter.blur = glowSource.blur * multiplier;
                effects.bloomFilter.bloomScale = glowSource.scale;
                effects.bloomFilter.enabled = true;

                if (isInnerGlow(glowSource.glowType)) {
                    effects.waveContainer.alpha = 0;
                } else {
                    effects.waveContainer.alpha = 1;
                }
            } else if (effects.bloomFilter) {
                effects.bloomFilter.enabled = false;

                // Global glow controls waveContainer alpha for inner/outer
                if (globalGlow.enabled && isInnerGlow(globalGlow.glowType)) {
                    effects.waveContainer.alpha = 0;
                } else {
                    effects.waveContainer.alpha = 1;
                }
            }

            // Fire
            const fire = useCustom && settings.customOptions?.fireSettings && settings.customOptions.fireSettings.enabled ? settings.customOptions.fireSettings : this.config.fireSettings;
            if (effects.displacementFilter && effects.displacementSprite) {
                effects.displacementFilter.enabled = fire.enabled;
                const displacementScale = fire.enabled ? fire.intensity * multiplier : 0;
                effects.displacementFilter.scale = new PIXI.Point(displacementScale, displacementScale * 10);
                effects.displacementSprite.scale.y = fire.detail;
                effects.displacementSprite.scale.x = fire.detail;
            }

            // Shadow
            const shadow = useCustom && settings.customOptions?.dropShadowSettings && settings.customOptions.dropShadowSettings.enabled ? settings.customOptions.dropShadowSettings : this.config.dropShadowSettings;
            // Routing a shadow onto the glowContainer is how a layer declines to cast its own. For a
            // layer with a per-layer bloom that container holds its clone; for a layer served by the
            // shared source it is empty, so the only shadow drawn is the single one beneath the whole
            // stack. A layer with its OWN custom shadow stays on the per-layer route, because that
            // shadow deliberately belongs to that layer.
            const shadowRidesOnGlowContainer = usePerLayerBloom || !hasCustomShadow;
            const glowShadowEnabled = shadowRidesOnGlowContainer && shadow.enabled;
            const waveShadowEnabled = !shadowRidesOnGlowContainer && shadow.enabled;

            if (effects.glowShadowFilter1 && effects.glowShadowFilter2) {
                effects.glowShadowFilter1.enabled = glowShadowEnabled;
                effects.glowShadowFilter2.enabled = glowShadowEnabled;
                effects.glowShadowFilter1.blur = shadow.blur * multiplier;
                effects.glowShadowFilter2.blur = shadow.blur * multiplier;
                effects.glowShadowFilter1.alpha = shadow.opacity;
                effects.glowShadowFilter2.alpha = shadow.opacity;
                effects.glowShadowFilter1.color = shadow.color;
                effects.glowShadowFilter2.color = shadow.color;
            }

            if (effects.waveShadowFilter1 && effects.waveShadowFilter2) {
                effects.waveShadowFilter1.enabled = waveShadowEnabled;
                effects.waveShadowFilter2.enabled = waveShadowEnabled;
                effects.waveShadowFilter1.blur = shadow.blur * multiplier;
                effects.waveShadowFilter2.blur = shadow.blur * multiplier;
                effects.waveShadowFilter1.alpha = shadow.opacity;
                effects.waveShadowFilter2.alpha = shadow.opacity;
                effects.waveShadowFilter1.color = shadow.color;
                effects.waveShadowFilter2.color = shadow.color;
            }

            if (effects.waveShadowPaddingFilter) {
                effects.waveShadowPaddingFilter.enabled = waveShadowEnabled;
            }
        });
    }

    destroy() {
        this.logoContainer.destroy({ children: true });
        this.visualsContainer.destroy({ children: true });
    }

    getVisualizationOptions(settings) {
        const { customOptions } = settings;
        const useCustom = customOptions && customOptions.enabled;
        const rotationInRadians = useCustom ? settings.customOptions.rotation * (Math.PI / 180) : 0;

        return {
            rotation: rotationInRadians,
            waveType: useCustom ? customOptions.waveType : this.config.waveType,
            barWidth: useCustom ? customOptions.barWidth : this.config.barWidth,
            pointRadius: useCustom ? customOptions.pointRadius : this.config.pointRadius,
            waveStyle: useCustom ? customOptions.waveStyle : this.config.waveStyle,
            inverted: useCustom ? customOptions.inverted : this.config.inverted,
            reflection: useCustom ? customOptions.reflectionType : this.config.reflection,
            radius: baseRadius,
            // A custom layer keeps its own movement direction; otherwise the global Direction drives it.
            inwards: useCustom
                ? customOptions.movementDirection === MOVEMENT_DIRECTION.INWARD
                : this.config.movementDirection === MOVEMENT_DIRECTION.INWARD,
            hollowCenter: useCustom ? (customOptions.hollowCenter !== undefined ? customOptions.hollowCenter : false) : this.config.hollowCenter,
            // Transparent is a layer-level toggle, so it applies even when Customize is off.
            cutout: customOptions && customOptions.cutout !== undefined ? customOptions.cutout : false
        };
    }
}